import { useState } from "react";
import { Body1, Button, Link, MessageBar, MessageBarBody, MessageBarTitle, ProgressBar, Spinner, makeStyles, tokens } from "@fluentui/react-components";
import { Checkmark20Filled, Folder20Regular, FolderAdd24Regular, FolderOpen20Regular } from "@fluentui/react-icons";
import type { FolderItem } from "@app/sp-data";
import { useData } from "../data/DataProvider";
import { useServizi } from "../data/useServizi";
import { creazioneInterrotta, loadLotti } from "../data/gare";
import { cartellePreviste, creaCartelle, impedimento, leggiModello, posizioneSalvata, type Esito, type Evento } from "../data/cartelle";
import { useAsync } from "../lib/useAsync";
import { goBack, replace } from "../lib/nav";
import { Shell } from "../components/Shell";
import { Section } from "../components/Section";
import { FolderPicker, type PickerState } from "../components/FolderPicker";
import { ErrorState, describeError } from "../components/ErrorState";

const useStyles = makeStyles({
  nomi: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS, margin: 0, paddingLeft: 0, listStyle: "none" },
  nome: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase300,
    overflowWrap: "anywhere",
  },
  fatta: { color: tokens.colorPaletteGreenForeground1 },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  posizione: { fontWeight: tokens.fontWeightSemibold, overflowWrap: "anywhere" },
  cols: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 3fr) minmax(320px, 2fr)",
    gap: tokens.spacingHorizontalL,
    alignItems: "start",
    "@media (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  side: { position: "sticky", top: "72px", display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM },
  dest: { display: "flex", flexDirection: "column", gap: "2px", paddingTop: tokens.spacingVerticalS, borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
});

type Fase = { tipo: "scelta" } | { tipo: "lavoro"; eventi: Evento[] } | { tipo: "fatto"; esito: Esito } | { tipo: "errore"; testo: string };

/** Creazione delle cartelle di gara: posizione → anteprima → avanzamento. */
export function CreaCartelle({ id }: { id: number }) {
  const s = useStyles();
  const data = useData();
  const { byId } = useServizi();
  const base = useAsync(async () => {
    const g = await data.gare.get(id);
    if (!g) throw new Error("Gara non trovata.");
    const [lotti, modello, root, salvata] = await Promise.all([
      g.fields.PerLotti ? loadLotti(data, id) : Promise.resolve([]),
      leggiModello(data),
      data.repository.root(),
      posizioneSalvata(data, g),
    ]);
    return { g, lotti, modello, root, salvata };
  }, [data, id]);
  const [picker, setPicker] = useState<PickerState>();
  const [fase, setFase] = useState<Fase>({ tipo: "scelta" });

  if (base.error || !base.data)
    return (
      <Shell title="Crea cartelle" onBack={() => goBack()}>
        {base.error ? <ErrorState error={base.error} /> : <Spinner style={{ marginTop: 48 }} label="Lettura del modello…" />}
      </Shell>
    );

  const { g, lotti, modello, root, salvata } = base.data;
  const servizio = byId.get(g.fields.Servizio ?? 0);
  if (!servizio) return <Spinner style={{ marginTop: 48 }} />;
  const blocco = impedimento(g, lotti, servizio);
  const previste = cartellePreviste(g, lotti, servizio);
  const posizione: FolderItem | undefined = salvata ?? (picker && !picker.invalid ? picker.current : undefined);

  async function avvia() {
    if (!posizione) return;
    setFase({ tipo: "lavoro", eventi: [] });
    try {
      const esito = await creaCartelle(data, g, servizio!, posizione, (e) =>
        setFase((f) => (f.tipo === "lavoro" ? { tipo: "lavoro", eventi: [...f.eventi, e] } : f)),
      );
      setFase({ tipo: "fatto", esito });
    } catch (e) {
      setFase({ tipo: "errore", testo: describeError(e).detail });
      void base.reload();
    }
  }

  if (fase.tipo === "lavoro") {
    const ultimo = [...fase.eventi].reverse().find((e) => e.tipo === "avanzamento");
    const finite = fase.eventi.filter((e) => e.tipo === "fine").map((e) => (e.tipo === "fine" ? e.nome : ""));
    const corrente = [...fase.eventi].reverse().find((e) => e.tipo === "inizio");
    return (
      <Shell title={`Crea cartelle · ${g.fields.Codice}`}>
        <Section title="Creazione in corso">
          <Body1>Non chiudere questa scheda finché non è finito.</Body1>
          {fase.eventi.length <= 1 && <Spinner size="small" label="Lettura del modello…" labelPosition="after" />}
          {corrente?.tipo === "inizio" && (
            <Body1>
              Cartella {corrente.indice + 1} di {corrente.totale}: <strong>{corrente.nome}</strong>
            </Body1>
          )}
          {ultimo?.tipo === "avanzamento" && <ProgressBar value={ultimo.fatte / ultimo.totali} thickness="large" />}
          <ul className={s.nomi}>
            {finite.map((n) => (
              <li key={n} className={s.nome}>
                <Checkmark20Filled className={s.fatta} /> {n}
              </li>
            ))}
          </ul>
        </Section>
      </Shell>
    );
  }

  if (fase.tipo === "fatto")
    return (
      <Shell title={`Cartelle · ${g.fields.Codice}`} onBack={() => replace(`/g/${g.id}`)}>
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Fatto</MessageBarTitle>
            {fase.esito.cartelle.length === 1 ? "La cartella di gara è pronta." : `Le ${fase.esito.cartelle.length} cartelle dei lotti sono pronte.`}
          </MessageBarBody>
        </MessageBar>
        <Section title="Cartelle">
          <ul className={s.nomi}>
            {fase.esito.cartelle.map((c) => (
              <li key={c.nome}>
                <Link className={s.nome} href={c.url} target="_blank" rel="noreferrer">
                  <FolderOpen20Regular /> {c.nome}
                </Link>
              </li>
            ))}
          </ul>
          <span className={s.meta}>Il link è stato salvato nella scheda.</span>
        </Section>
        <div>
          <Button appearance="primary" onClick={() => replace(`/g/${g.id}`)}>
            Torna alla scheda
          </Button>
        </div>
      </Shell>
    );

  const pulsante = (
    <Button appearance="primary" size="large" icon={<FolderAdd24Regular />} disabled={!posizione || !!blocco} onClick={avvia}>
      {!posizione
        ? (picker?.invalid ?? "Scegli la posizione")
        : previste.filter((p) => !p.fatta).length > 1
          ? `Crea qui le ${previste.filter((p) => !p.fatta).length} cartelle`
          : "Crea qui la cartella"}
    </Button>
  );

  return (
    <Shell title={`Crea cartelle · ${g.fields.Codice}`} onBack={() => goBack()}>
      {fase.tipo === "errore" && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>La creazione si è interrotta</MessageBarTitle>
            {fase.testo} Le cartelle già create restano: puoi riprovare, riprende da dove si era fermata.
          </MessageBarBody>
        </MessageBar>
      )}
      {blocco && !creazioneInterrotta(g) && (
        <MessageBar intent="warning">
          <MessageBarBody>{blocco}</MessageBarBody>
        </MessageBar>
      )}

      <div className={s.cols}>
        <Section title="Dove crearle">
          {salvata ? (
            <Body1>
              {creazioneInterrotta(g) ? "La creazione era iniziata in " : "Le altre cartelle della gara sono in "}
              <span className={s.posizione}>{salvata.path}</span>: si crea lì.
            </Body1>
          ) : (
            <>
              <span className={s.meta}>Apri le cartelle fino all'ente (stazione appaltante o ente dell'albo). Se l'ente non c'è, crealo con "Nuova cartella".</span>
              <FolderPicker folders={data.repository} root={root} modello={modello} onChange={setPicker} />
            </>
          )}
        </Section>

        <div className={s.side}>
          <Section title={previste.length > 1 ? "Cartelle che verranno create" : "Cartella che verrà creata"}>
            <ul className={s.nomi}>
              {previste.map((p) => (
                <li key={p.nome} className={s.nome}>
                  {p.fatta ? <Checkmark20Filled className={s.fatta} /> : <Folder20Regular />} {p.nome}
                  {p.fatta && <span className={s.meta}>(già creata)</span>}
                </li>
              ))}
            </ul>
            <span className={s.meta}>
              {previste.length > 1 ? "Ognuna con le" : "Con le"} {contaCartelle(modello)} sottocartelle del modello. I file del modello non vengono copiati.
            </span>
            <div className={s.dest}>
              <span className={s.meta}>Destinazione</span>
              <span className={s.posizione}>{posizione ? `Repository/${posizione.path}` : "—"}</span>
            </div>
            {pulsante}
          </Section>
        </div>
      </div>
    </Shell>
  );
}

function contaCartelle(n: Array<{ children: unknown[] }>): number {
  return n.reduce((t, x) => t + 1 + contaCartelle(x.children as Array<{ children: unknown[] }>), 0);
}
